import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { isObjectEmpty, stringIsNullOrEmpty } from '../../../common/util';
import { useTranslation } from 'react-i18next';
import { colorError } from '../../../shared/theme';
import { ApiKey, Page, PaymentStatus, VendorApiUrl } from '../../../common/constant';
import ApiEngine from '../../../common/api_engine';
import moment from 'moment';
import { QRCode, Spin, Typography } from 'antd';

/**
 * @author Nelson
 * @returns 
 */
const DemoDuitnow = () => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const amount = searchParams.get("amount");
    const signature = searchParams.get("signature");
    const navigate = useNavigate();
    const [timeLeft, setTimeLeft] = useState(null);
    const [qrValidUntil, setQrValidUntil] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qrDetail, setQrDetail] = useState(null);
    const [txnText, setTxnText] = useState('');

    /**
     * @author Nelson
     */
    useEffect(() => {
        let intervalId;
        const initPayment = async () => {
            try {
                setLoading(true);

                if (!stringIsNullOrEmpty(signature) && !isNaN(amount) && parseFloat(amount) > 0 && parseFloat(amount) < 500) {
                    (
                        async () => {
                            let response = await ApiEngine.post(`${VendorApiUrl._DEMO_DUITNOW_INIT}?amount=${amount}&signature=${signature}`, {});
                            if (response[ApiKey._SUCCESS_KEY] && !isObjectEmpty(response[ApiKey._DATA_KEY])) {
                                setQrDetail(response[ApiKey._DATA_KEY]);
                                setQrValidUntil(moment().add(3, "minutes").format("YYYY-MM-DD HH:mm:ss"));

                                const txnId = response[ApiKey._DATA_KEY]['txnId'];
                                setTxnText(txnId);

                                const checkPaymentStatus = async (txnId) => {
                                    try {
                                        response = await ApiEngine.get(`${VendorApiUrl._DEMO_DUITNOW_POOL.replace(':txnId', txnId)}`);

                                        if (response.data.status === PaymentStatus._SUCCESS || response.data.status === PaymentStatus._FAILED) {
                                            clearInterval(intervalId);
                                            navigate(Page._PAYMENT_RESULT, {
                                                replace: true,
                                                state: { response: response }
                                            });
                                        }
                                    } catch (error) {
                                        handlePaymentStatus();
                                    }
                                };

                                intervalId = setInterval(async () => await checkPaymentStatus(txnId), 5000);
                            }
                            else {
                                throw '';
                            }
                        }
                    )();
                }
                else {
                    throw '';
                }
            } catch (error) {
                handlePaymentStatus();
            } finally {
                setLoading(false);
            }
        };

        initPayment();
        return () => clearInterval(intervalId);
    }, [signature, amount]);

    /// <summary>
    /// Author: Wei Xiang
    /// </summary>
    useEffect(() => {
        let interval
        if (qrValidUntil !== null) {

            interval = setInterval(() => {
                const remainingTime = calculateTimeLeft(qrValidUntil);
                setTimeLeft(remainingTime);

                if (qrValidUntil !== null && remainingTime <= 0) {
                    clearInterval(interval);

                    (
                        async () => {
                            await ApiEngine.get(`${VendorApiUrl._DEMO_DUITNOW_POOL.replace(':txnId', txnText)}?terminate=true`);
                        }
                    )();

                    const response = {
                        title: t("PAYMENT_FAILED"),
                        message: t("QR_CODE_TIMEOUT"),
                        icon: {
                            name: 'times-circle',
                            color: colorError,
                        },
                    };
                    navigate(Page._PAYMENT_RESULT, { state: { response }, replace: true });
                }
            }, 1000);
        }

        return () => clearInterval(interval);
    }, [qrValidUntil, txnText]);

    function calculateTimeLeft(expiry) {
        const now = new Date();
        const expiryTime = new Date(expiry);
        const diff = expiryTime - now;
        return diff > 0 ? diff : 0;
    }

    function formatTime(milliseconds) {
        const totalSeconds = Math.floor(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    const handlePaymentStatus = () => {
        const response = {
            title: t("PAYMENT_FAILED"),
            message: t("CONTACT_ADMIN_FOR_HELP"),
            icon: {
                name: 'times-circle',
                color: colorError,
            },
        };

        navigate(Page._PAYMENT_RESULT, { state: { response }, replace: true });
    };

    return (
        <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '100dvh',
            backgroundColor: '#f0f2f5'
        }}>
            {loading || timeLeft === null ? (
                <Spin size="large" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }} />
            ) :
                <div style={{
                    width: '320px',
                    padding: '20px 20px 35px 20px',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}>
                    <img src={require('../../../assets/images/duitnow.png')} width={"70px"} />
                    <Typography.Text style={{ textAlign: "center", marginTop: 12 }}>Kindly scan the QR code below to proceed with payment</Typography.Text>
                    <Typography.Text style={{ textAlign: "center" }}>Please do not refresh the page</Typography.Text>
                    <Typography.Text style={{ textAlign: "center", marginTop: 4, marginBottom: 12, fontWeight: "bold" }}>Expires in {formatTime(timeLeft)}</Typography.Text>
                    <div style={{ background: "#ed2a67", padding: 12, textAlign: "center", borderRadius: "8px" }}>
                        <Typography.Title level={4} style={{ margin: 0, marginBottom: 12, color: 'white' }}>RM {parseFloat(amount).toFixed(2)}</Typography.Title>
                        <QRCode value={qrDetail?.qrPayload} size={240} status={qrDetail?.qrPayload ? "active" : "loading"} color={"#ed2a67"} bgColor={"white"} style={{ marginBottom: 8 }} />
                        <Typography.Text style={{ color: 'white', fontWeight: "bold" }}>MALAYSIA NATIONAL QR</Typography.Text>
                    </div>
                </div >}
        </div >
    )
};

export default DemoDuitnow;